import { css } from "@emotion/css"
import { useQuery } from "react-query"
import { http } from "api/http"

import { Loading } from "@dashboards/components/Loading"
import { Empty } from "@dashboards/components/Empty"
import { DataEnvelop } from "@dashboards/helpers/api"
import { Card, CardTitle } from "@dashboards/components/Card"
import { ResponsiveWorldBubbles } from "@dashboards/components/geo/WorldBubbles"
import { Leaderboard } from "@dashboards/components/html/Leaderboard"
import { customSGDFormat } from "@dashboards/helpers/formatters"
import useFilters from "../filters/hook"
import {
  NetGrossTotalFilter,
  useNetGrossTotalFilterState,
  KindOfAmount,
} from "../filters/NetGrossTotal"

const styles = {
  card: css`
    min-height: 400px;
  `,
  container: css`
    padding: var(--spacing-l, 20px) 0 0 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: var(--spacing-l, 20px);
  `,
  world: css`
    height: 380px;
  `,
  chart: css`
    padding: var(--spacing-xxl-2);
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  `,
}

export default function SpendByMarket() {
  const { getSearchParams } = useFilters({ filters: ["startDate", "endDate"] })
  const { data, status } = useQuery(
    ["reporting/regional/spend-by-market", getSearchParams()],
    fetchData,
  )
  const datadata = data?.data ?? []
  const isEmpty = status === "success" && datadata.length === 0

  const ngt = useNetGrossTotalFilterState()
  const kindOfAmount = ngt.value
  const amountKey = amountKeyMap[kindOfAmount]

  return (
    <Card className={styles.card}>
      <header className={styles.header}>
        <CardTitle>Market Spend Breakdown</CardTitle>
        <NetGrossTotalFilter {...ngt} />
      </header>
      {status === "loading" ? (
        <Loading />
      ) : isEmpty ? (
        <Empty />
      ) : status === "error" ? (
        <Empty title="something went wrong" />
      ) : (
        <div className={styles.container}>
          <div className={styles.world}>
            <ResponsiveWorldBubbles
              data={datadata}
              getCountry={(x) => x.marketName}
              getValue={(x) => x[amountKey]}
            />
          </div>
          <div className={styles.chart}>
            <Leaderboard
              data={datadata}
              getName={(x) => x.marketName}
              getValue={(x) => x[amountKey]}
              formatValue={customSGDFormat}
              className={css`
                .name {
                  max-width: 180px;
                }
              `}
              bar={{
                trackColor: "#e0f5f4",
              }}
            />
          </div>
        </div>
      )}
    </Card>
  )
}

const amountKeyMap: Record<KindOfAmount, keyof Omit<Item, "marketName">> = {
  net: "netSpend",
  gross: "grossSpend",
  total: "totalSpend",
}

type Item = {
  marketName: string
  totalSpend: number
  netSpend: number
  grossSpend: number
}

type Response = DataEnvelop<Item>

const fetchData = async (key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res: Response = await http(key, { searchParams, timeout: false }).json()
  return res
}
